// src/features/member/memberSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { LoanRepository } from '../modules/Loan/repository/Loan';
import { ExtraLoanDetails } from '../modules/Loan/types/ExtraLoanDetails';
import { MemberDataTableRepository } from '../modules/Members/repository/DataTable';
import { MembersRepository } from '../modules/Members/repository/Members';
import { ExtraClientDetails } from '../modules/Members/types/data-tables/ExtraClientDetails';
import { Member } from '../modules/Members/types/response/ListMembersResponse';
import { SavingsRepository } from '../modules/Savings/repository/Savings';
import { ClientAccounts } from '../modules/Savings/types/response/ListClientSavings';
import { setLoanRecipientAccounts } from './loanSlice';
import { AppDispatch, RootState } from './store';

// ───────────────────────────────
// Async thunks
// ───────────────────────────────

export const fetchMember = createAsyncThunk<Member, number, { rejectValue: string }>(
	'member/fetchMember',
	async (memberId, { rejectWithValue }) => {
		try {
			return await MembersRepository.getClient(memberId);
		} catch (err: any) {
			return rejectWithValue(err?.message || 'Failed to load member');
		}
	},
);

export const fetchMemberPhoto = createAsyncThunk<string, number, { rejectValue: string }>(
	'member/fetchMemberPhoto',
	async (memberId, { rejectWithValue }) => {
		try {
			return await MembersRepository.getClientImage(memberId.toString());
		} catch (err: any) {
			return rejectWithValue(err?.message || 'Failed to load photo');
		}
	},
);

export const fetchMemberExtraDetails = createAsyncThunk<
	ExtraClientDetails | null,
	number,
	{ rejectValue: string }
>('member/fetchMemberExtraDetails', async (memberId, { rejectWithValue }) => {
	try {
		const data = await MemberDataTableRepository.getDataTableEntry<ExtraClientDetails>({
			clientId: memberId,
			dataTable: 'extra_client_details',
		});
		return data.length > 0 ? data[0] : null;
	} catch (err: any) {
		return rejectWithValue(err?.message || 'Failed to load extra details');
	}
});

export const fetchMemberAccounts = createAsyncThunk<
	ClientAccounts,
	number | string,
	{ rejectValue: string; dispatch: AppDispatch }
>('member/fetchMemberAccounts', async (memberId, { rejectWithValue, dispatch }) => {
	try {
		const data = await SavingsRepository.listClientAccounts(memberId);

		// Fetch extra loan details in parallel
		const promises = data.loanAccounts?.map(async (loan, index) => {
			try {
				const details = await LoanRepository.getDataTableEntry<ExtraLoanDetails>({
					loanId: loan.id!,
					dataTable: 'extra_loan_details',
					data: {},
				});
				data.loanAccounts![index].extraLoanDetails = details[0];
			} catch {
				// ignore
			}
		});
		if (promises) await Promise.all(promises);

		// update loan slice too
		dispatch(setLoanRecipientAccounts(data));

		return data;
	} catch (err: any) {
		return rejectWithValue(err?.message || 'Failed to load accounts');
	}
});

// ───────────────────────────────
// State Shape
// ───────────────────────────────

export interface MemberState {
	data?: Member;
	photo?: string;
	extraDetails?: ExtraClientDetails | null;
	accounts?: ClientAccounts;

	loading: {
		member: boolean;
		photo: boolean;
		extraDetails: boolean;
		accounts: boolean;
	};

	error: {
		member?: string;
		photo?: string;
		extraDetails?: string;
		accounts?: string;
	};
}

const initialState: MemberState = {
	loading: {
		member: false,
		photo: false,
		extraDetails: false,
		accounts: false,
	},
	error: {},
};

// ───────────────────────────────
// Slice
// ───────────────────────────────

const memberSlice = createSlice({
	name: 'member',
	initialState,
	reducers: {
		resetMemberState: () => initialState,
	},
	extraReducers: builder => {
		builder
			// Member
			.addCase(fetchMember.pending, state => {
				state.loading.member = true;
				state.error.member = undefined;
			})
			.addCase(fetchMember.fulfilled, (state, action) => {
				state.loading.member = false;
				state.data = action.payload;
			})
			.addCase(fetchMember.rejected, (state, action) => {
				state.loading.member = false;
				state.error.member = action.payload;
			})

			// Photo
			.addCase(fetchMemberPhoto.pending, state => {
				state.loading.photo = true;
				state.error.photo = undefined;
			})
			.addCase(fetchMemberPhoto.fulfilled, (state, action) => {
				state.loading.photo = false;
				state.photo = action.payload;
			})
			.addCase(fetchMemberPhoto.rejected, (state, action) => {
				state.loading.photo = false;
				state.error.photo = action.payload;
			})

			// Extra Details
			.addCase(fetchMemberExtraDetails.pending, state => {
				state.loading.extraDetails = true;
				state.error.extraDetails = undefined;
			})
			.addCase(fetchMemberExtraDetails.fulfilled, (state, action) => {
				state.loading.extraDetails = false;
				state.extraDetails = action.payload;
			})
			.addCase(fetchMemberExtraDetails.rejected, (state, action) => {
				state.loading.extraDetails = false;
				state.error.extraDetails = action.payload;
			})

			// Accounts
			.addCase(fetchMemberAccounts.pending, state => {
				state.loading.accounts = true;
				state.error.accounts = undefined;
			})
			.addCase(fetchMemberAccounts.fulfilled, (state, action) => {
				state.loading.accounts = false;
				state.accounts = action.payload;
			})
			.addCase(fetchMemberAccounts.rejected, (state, action) => {
				state.loading.accounts = false;
				state.error.accounts = action.payload;
			});
	},
});

// ───────────────────────────────
// Exports
// ───────────────────────────────

export const { resetMemberState } = memberSlice.actions;
export const selectMemberState = (state: RootState) => state.member;
export default memberSlice.reducer;
